import { DateTime } from 'luxon';
import { TableLazyLoadEvent } from 'primeng/table';

import { CURRENT_DATE_FORMAT, DEFAULT_DATE_FORMAT } from '../../constants';
import {
  SortingConfig,
  SortingDirection,
  SortingMode,
  SortingRule,
} from '../../models';

const DATE_REGEX_DASH = /^(0[1-9]|[1-2][0-9]|3[0-1])-(0[1-9]|1[0-2])-\d{4}$/;
const DATE_REGEX_DOT = /^(0[1-9]|[1-2][0-9]|3[0-1])\.(0[1-9]|1[0-2])\.\d{4}$/;

const isDate = <T>(value: T): boolean => {
  if (typeof value !== 'string') return false;

  return DATE_REGEX_DASH.test(value) || DATE_REGEX_DOT.test(value);
};

const parseDateString = (value: string): DateTime => {
  if (DATE_REGEX_DASH.test(value)) {
    return DateTime.fromFormat(value, DEFAULT_DATE_FORMAT);
  }

  if (DATE_REGEX_DOT.test(value)) {
    return DateTime.fromFormat(value, CURRENT_DATE_FORMAT);
  }

  return DateTime.invalid('unparseable');
};

const isNullOrEmpty = <T>(value: T) => value == null || value === '';

const isNumericString = (value: unknown): boolean =>
  typeof value === 'string' &&
  value.trim() !== '' &&
  !Number.isNaN(Number(value));

export const compareGridValues = <T>(
  a: T,
  b: T,
  direction: SortingDirection,
): number => {
  const order = direction === SortingDirection.Ascending ? 1 : -1;

  if (isNullOrEmpty(a) && !isNullOrEmpty(b)) return 1 * order;
  if (!isNullOrEmpty(a) && isNullOrEmpty(b)) return -1 * order;
  if (isNullOrEmpty(a) && isNullOrEmpty(b)) return 0;

  if (isDate(a) && isDate(b)) {
    const dateA = parseDateString(a as string);
    const dateB = parseDateString(b as string);

    if (dateA.isValid && dateB.isValid) {
      if (dateA < dateB) return -1 * order;
      if (dateA > dateB) return 1 * order;

      return 0;
    }
  }

  if (isNumericString(a) && isNumericString(b)) {
    const numA = Number(a);
    const numB = Number(b);
    if (numA < numB) return -1 * order;
    if (numA > numB) return 1 * order;

    return 0;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b) * order;
  }

  if (a < b) return -1 * order;
  if (a > b) return 1 * order;

  return 0;
};

export const sortData = <T extends Record<string, any>>(
  data: T[],
  config: SortingConfig,
): T[] => {
  if (data.length === 0 || config.rules.length === 0) return data;

  const { mode, rules } = config;

  if (mode === SortingMode.Single) {
    const { field, direction } = rules[0];

    return data
      .slice()
      .sort((a, b) => compareGridValues(a[field], b[field], direction));
  }

  return data.slice().sort((a, b) =>
    rules.reduce((comparison, { field, direction }) => {
      if (comparison !== 0) return comparison;

      return compareGridValues(a[field], b[field], direction);
    }, 0),
  );
};

export const createSortingConfig = (
  event: TableLazyLoadEvent,
): SortingConfig => {
  const { sortField, multiSortMeta, sortOrder } = event;

  const mode =
    sortField !== undefined && !multiSortMeta
      ? SortingMode.Single
      : SortingMode.Multiple;

  if (!sortField && !multiSortMeta) {
    return {
      mode: SortingMode.Single,
      rules: [],
    };
  }

  let rules: SortingRule[] = [];

  if (mode === SortingMode.Single && sortField && sortOrder !== undefined) {
    rules = [
      {
        field: sortField as string,
        direction:
          sortOrder === 1
            ? SortingDirection.Ascending
            : SortingDirection.Descending,
      },
    ];
  } else if (mode === SortingMode.Multiple && multiSortMeta?.length) {
    rules = multiSortMeta
      .filter((meta) => meta.field && meta.order !== undefined)
      .map((meta) => ({
        field: meta.field as string,
        direction:
          meta.order === 1
            ? SortingDirection.Ascending
            : SortingDirection.Descending,
      }));
  }

  return { mode, rules };
};
